import { Box } from '@chakra-ui/react';
import { type SortableItem, DropResult, Sortable } from '@diamond/shared/ui';
import { useMemo } from 'react';

import type { VoucherListItem, VoucherStatus } from '../types';
import { VoucherItem } from './voucher-item';
import { VoucherSectionHeading } from './voucher-section-heading';

interface VoucherListProps {
  data: VoucherListItem[];
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onIndexChange: (result: DropResult) => void;
  onMoveToTop: (id: string) => void;
  onToggleStatus: (id: string, status: VoucherStatus) => void;
  onToggleDisplay: (id: string, isDisplay: boolean) => void;
  deleteLoadingId?: string | null;
  statusLoadingId?: string | null;
  displayLoadingId?: string | null;
  currentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
}

export function VoucherList({
  data,
  onDelete,
  onEdit,
  onDuplicate,
  onIndexChange,
  onMoveToTop,
  onToggleStatus,
  onToggleDisplay,
  deleteLoadingId,
  statusLoadingId,
  displayLoadingId,
  currentPage = 1,
  totalItems,
  itemsPerPage = 10,
}: VoucherListProps) {
  const items: SortableItem[] = useMemo(
    () =>
      data.map((voucher, index) => {
        // Calculate the global/original index based on current page
        const originalIndex = (currentPage - 1) * itemsPerPage + index;

        return {
          id: voucher.id,
          originalIndex,
          children: (
            <VoucherItem
              item={voucher}
              onDelete={onDelete}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onToggleStatus={onToggleStatus}
              onToggleDisplay={onToggleDisplay}
              isDeleteLoading={deleteLoadingId === voucher.id}
              isStatusLoading={statusLoadingId === voucher.id}
              isDisplayLoading={displayLoadingId === voucher.id}
            />
          ),
        };
      }),
    [
      data,
      currentPage,
      itemsPerPage,
      onDelete,
      onEdit,
      onDuplicate,
      onToggleStatus,
      onToggleDisplay,
      deleteLoadingId,
      statusLoadingId,
      displayLoadingId,
    ]
  );

  return (
    <Box my={8} width="full">
      <VoucherSectionHeading />
      <Sortable
        data={items}
        handleChange={onIndexChange}
        handleMoveToTop={onMoveToTop}
        showMoveToTopOnLastItems
        totalItems={totalItems}
      />
    </Box>
  );
}
